import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./analisiDrawdownChartsCompare.module.scss";
import { formatLocalizedDate } from "utils/dateUtils";

type ComponentProps = { data: any; data2?: any };

const AnalisiDrawdownChartsCompare: React.FC<ComponentProps> = ({
  data,
  data2,
}) => {
  const { t, i18n } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const chartData = useMemo(() => {
    if (!data || !data2) return [];

    const portfolioData =
      data.analisi_backtest.simulazione_andamento_portafoglio;
    const portfolioData2 =
      data2.analisi_backtest.simulazione_andamento_portafoglio;

    const downsample = (portfolioData: any[], interval = 1) =>
      portfolioData.filter((_, index) => index % interval === 0);
    const downsampledData = downsample(portfolioData);
    const downsampledData2 = downsample(portfolioData2);

    const maxDrawdownStats =
      data.analisi_backtest.analisi_drawdown_max_drawdown_stats;
    const recoveryTimeStats =
      data.analisi_backtest.analisi_drawdown_recovery_time_stats;
    const maxDrawdownStats2 =
      data2.analisi_backtest.analisi_drawdown_max_drawdown_stats;
    const recoveryTimeStats2 =
      data2.analisi_backtest.analisi_drawdown_recovery_time_stats;

    const hasDrawdown =
      maxDrawdownStats && maxDrawdownStats[1]?.weight !== null;
    const hasDrawdown2 =
      maxDrawdownStats2 && maxDrawdownStats2[1]?.weight !== null;

    const drawdownStartDate = hasDrawdown
      ? new Date(maxDrawdownStats[1]?.weight)
      : null;
    const drawdownEndDate = hasDrawdown
      ? new Date(maxDrawdownStats[2]?.weight)
      : null;
    const drawdownStartDate2 = hasDrawdown2
      ? new Date(maxDrawdownStats2[1]?.weight)
      : null;
    const drawdownEndDate2 = hasDrawdown2
      ? new Date(maxDrawdownStats2[2]?.weight)
      : null;

    const hasRecovery =
      hasDrawdown && recoveryTimeStats && recoveryTimeStats[2]?.weight !== null;
    const hasRecovery2 =
      hasDrawdown2 &&
      recoveryTimeStats2 &&
      recoveryTimeStats2[2]?.weight !== null;

    const recoveryEndDate = hasRecovery
      ? new Date(recoveryTimeStats[2]?.weight)
      : null;
    const recoveryEndDate2 = hasRecovery2
      ? new Date(recoveryTimeStats2[2]?.weight)
      : null;

    const processedData = downsampledData.map((point) => {
      const dateObj = new Date(point.date);
      return {
        ...point,
        originalDate: point.date,
        date: dateObj.toISOString(),
        value: parseFloat(point.value),
      };
    });
    const processedData2 = downsampledData2.map((point) => {
      const dateObj = new Date(point.date);
      return {
        ...point,
        originalDate: point.date,
        date: dateObj.toISOString(),
        value: parseFloat(point.value),
      };
    });

    const map = new Map();

    for (const point of processedData) {
      const pointDate = new Date(point.originalDate);
      const item: any = {
        date: pointDate.toISOString(),
        originalDate: point.originalDate,
        normalValueA: null,
        drawdownValueA: null,
        recoveryValueA: null,
        normalAfterValueA: null,
        normalValueB: null,
        drawdownValueB: null,
        recoveryValueB: null,
        normalAfterValueB: null,
      };

      if (hasDrawdown) {
        if (pointDate < drawdownStartDate!) item.normalValueA = point.value;
        else if (pointDate <= drawdownEndDate!)
          item.drawdownValueA = point.value;
        else if (hasRecovery && pointDate <= recoveryEndDate!)
          item.recoveryValueA = point.value;
        else item.normalAfterValueA = point.value;
      } else {
        item.normalValueA = point.value;
      }

      map.set(point.originalDate, item);
    }

    for (const point of processedData2) {
      const pointDate = new Date(point.originalDate);
      const item = map.get(point.originalDate) || {
        date: pointDate.toISOString(),
        originalDate: point.originalDate,
        normalValueA: null,
        drawdownValueA: null,
        recoveryValueA: null,
        normalAfterValueA: null,
        normalValueB: null,
        drawdownValueB: null,
        recoveryValueB: null,
        normalAfterValueB: null,
      };

      if (hasDrawdown2) {
        if (pointDate < drawdownStartDate2!) item.normalValueB = point.value;
        else if (pointDate <= drawdownEndDate2!)
          item.drawdownValueB = point.value;
        else if (hasRecovery2 && pointDate <= recoveryEndDate2!)
          item.recoveryValueB = point.value;
        else item.normalAfterValueB = point.value;
      } else {
        item.normalValueB = point.value;
      }

      map.set(point.originalDate, item);
    }

    return Array.from(map.values()).sort(
      (a, b) =>
        new Date(a.originalDate).getTime() - new Date(b.originalDate).getTime(),
    );
  }, [data, data2]);

  // New data processing for second chart
  const secondChartData = useMemo(() => {
    if (!data) return [];

    // Process data from first source
    const drawdownDataA = data.analisi_backtest.analisi_drawdown.map(
      (item: any) => ({
        ...item,
        date: new Date(item.date).toISOString(),
        valueA: item.value * 1,
        valueB: null,
      }),
    );

    // If data2 exists, process and merge its data
    if (data2 && data2.analisi_backtest.analisi_drawdown) {
      const drawdownDataB = data2.analisi_backtest.analisi_drawdown.map(
        (item: any) => ({
          date: new Date(item.date).toISOString(),
          valueB: item.value * 1,
        }),
      );

      // Merge the data, prioritizing dates that exist in both datasets
      const mergedData = [...drawdownDataA];
      drawdownDataB.forEach((pointB) => {
        const existingPoint = mergedData.find(
          (pointA) => pointA.date === pointB.date,
        );
        if (existingPoint) {
          existingPoint.valueB = pointB.valueB;
        } else {
          mergedData.push({
            date: pointB.date,
            valueA: null,
            valueB: pointB.valueB,
          });
        }
      });

      // Sort by date
      return mergedData.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );
    }

    return drawdownDataA;
  }, [data, data2]);

  const allValues = chartData.flatMap((d) =>
    [
      d.normalValueA,
      d.drawdownValueA,
      d.recoveryValueA,
      d.normalAfterValueA,
      d.normalValueB,
      d.drawdownValueB,
      d.recoveryValueB,
      d.normalAfterValueB,
    ].filter((v) => v !== null),
  );
  const rawMin = Math.min(...allValues);
  const rawMax = Math.max(...allValues);

  const tickStep = 10;

  const minValue = Math.floor(rawMin / tickStep) * tickStep;
  const maxValue = Math.ceil(rawMax / tickStep) * tickStep;

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const entry = payload[0].payload;
    const originalDate = new Date(entry.originalDate);
    const formattedDate = formatLocalizedDate(originalDate, i18n.language);

    return (
      <div className={styles.tooltip}>
        <p className={styles.tooltipLabel}>{formattedDate}</p>
        {["A", "B"].map((key) => {
          const value =
            entry[`normalValue${key}`] ??
            entry[`drawdownValue${key}`] ??
            entry[`recoveryValue${key}`] ??
            entry[`normalAfterValue${key}`];
          let portfolioName = "";
          if (key === "A") {
            portfolioName = t("portafoglioCompare.portafoglio1");
          } else {
            portfolioName = t("portafoglioCompare.portafoglio2");
          }
          if (value === null || value === undefined) return null;
          return (
            <p
              key={key}
              style={{
                color:
                  key === "A"
                    ? colorsConfig.portfolio1
                    : colorsConfig.portfolio2,
                fontSize: "12px",
                margin: 0,
              }}
            >
              {/* {`${portfolioName}: ${value.toFixed(2)}`} */}
              {`${portfolioName}: ${value.toFixed(2)}%`}
            </p>
          );
        })}
      </div>
    );
  };

  const SecondChartTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className={styles.tooltip}>
        {payload.map((entry: any, index: number) => {
          if (entry.value === null || entry.value === undefined) return null;
          return (
            <p
              key={index}
              style={{
                margin: 0,
                color:
                  entry.name === "valueA"
                    ? colorsConfig.portfolio1
                    : colorsConfig.portfolio2,
              }}
            >
              {`${entry.name === "valueA" ? t("portafoglioCompare.portafoglio1") : t("portafoglioCompare.portafoglio2")}: ${entry.value.toFixed(2)}%`}
            </p>
          );
        })}
      </div>
    );
  };

  const CustomizedLegend = (props: any) => {
    if (!data2) return null;
    const legendItems = [
      {
        value: t("portafoglioCompare.portafoglio1"),
        color: colorsConfig.portfolio1,
      },
      {
        value: t("portafoglioCompare.portafoglio2"),
        color: colorsConfig.portfolio2,
      },
    ];

    return (
      <div className={styles.legendContainer}>
        {legendItems.map((entry, index) => (
          <div key={`item-${index}`} className={styles.legendItem}>
            <div
              className={styles.legendDot}
              style={{ backgroundColor: entry.color }}
            />
            <span className={styles.legendCopy}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };
  return (
    <div className={styles.container}>
      <div className={styles.chartContainer}>
        <div className={styles.chartContainerInner}>
          <ResponsiveContainer width="100%" height={300} debounce={500}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 10, left: -25, bottom: 10 }}
            >
              <CartesianGrid
                strokeWidth={1}
                stroke={colorsConfig.GrayLight2}
                strokeOpacity={1}
                horizontal
                vertical={false}
              />
              <YAxis
                domain={[minValue, maxValue]}
                ticks={Array.from(
                  { length: Math.floor((maxValue - minValue) / tickStep) + 1 },
                  (_, i) => minValue + i * tickStep,
                )}
                tick={{
                  fontSize: 11,
                  fill: colorsConfig.finecoBlack,
                  fontFamily: "Proxima Nova",
                }}
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
                tickLine={false}
              />

              <Tooltip content={<CustomTooltip />} />

              {(["normal", "drawdown", "recovery", "normalAfter"] as const).map(
                (period, index) => (
                  <React.Fragment key={period}>
                    <Line
                      dataKey={`${period}ValueA`}
                      stroke={
                        period === "drawdown"
                          ? colorsConfig.red
                          : period === "recovery"
                            ? colorsConfig.Green
                            : colorsConfig.portfolio1
                      }
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                    <Line
                      dataKey={`${period}ValueB`}
                      strokeDasharray="4 2"
                      stroke={
                        period === "drawdown"
                          ? colorsConfig.red
                          : period === "recovery"
                            ? colorsConfig.Green
                            : colorsConfig.portfolio2
                      }
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                  </React.Fragment>
                ),
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Changed from AreaChart to LineChart */}
        <div className={styles.chartContainerInner}>
          <ResponsiveContainer width="100%" height={300} debounce={500}>
            <LineChart
              data={secondChartData}
              margin={{ top: 20, right: 10, left: -25, bottom: 10 }}
            >
              <CartesianGrid
                strokeWidth={1}
                stroke={colorsConfig.GrayLight2}
                strokeOpacity={1}
                horizontal
                vertical={false}
              />
              <XAxis
                dataKey="date"
                dx={10}
                dy={10}
                textAnchor="end"
                interval={250}
                tick={{
                  fontSize: 11,
                  fill: colorsConfig.finecoBlack,
                  fontFamily: "Proxima Nova",
                }}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString("it-IT", {
                    year: "numeric",
                  })
                }
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
                tickLine={false}
              />
              <YAxis
                tick={{
                  fontSize: 11,
                  fill: colorsConfig.finecoBlack,
                  fontFamily: "Proxima Nova",
                }}
                tickFormatter={(value) => `${value}`}
                domain={["auto", "auto"]}
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
                tickLine={false}
              />
              <Tooltip content={<SecondChartTooltip />} />
              <Legend content={<CustomizedLegend />} />
              <Line
                type="basis"
                dataKey="valueA"
                name="valueA"
                stroke={colorsConfig.portfolio1}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="basis"
                dataKey="valueB"
                name="valueB"
                stroke={colorsConfig.portfolio2}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalisiDrawdownChartsCompare;
